import { Organisation } from '@/types/organisation.types';
import { create } from 'zustand';

const STORAGE_KEY = 'openpaye_current_org';

interface OrganisationsState {
  organisations: Organisation[];
  currentOrganisation: Organisation | null;
  isLoading: boolean;
  error: string | null;
}

interface OrganisationsActions {
  setOrganisations: (orgs: Organisation[]) => void;
  setCurrentOrganisation: (org: Organisation | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

function loadPersistedOrg(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

const initialState: OrganisationsState = {
  organisations: [],
  currentOrganisation: null,
  isLoading: false,
  error: null,
};

export const useOrganisationsStore = create<
  OrganisationsState & OrganisationsActions
>()((set, get) => ({
  ...initialState,

  setOrganisations: (organisations) => {
    set({ organisations });

    // Auto-select persisted or first org if none selected
    const current = get().currentOrganisation;
    if (!current && organisations.length > 0) {
      const persistedUuid = loadPersistedOrg();
      const match = persistedUuid
        ? organisations.find((o) => o.uuid === persistedUuid)
        : null;
      const org = match || organisations[0];
      set({ currentOrganisation: org });
      try {
        localStorage.setItem(STORAGE_KEY, org.uuid);
      } catch {
        // La sélection reste utilisable lorsque le stockage est indisponible.
      }
    }
  },

  setCurrentOrganisation: (org) => {
    set({ currentOrganisation: org });
    try {
      if (org) {
        localStorage.setItem(STORAGE_KEY, org.uuid);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // La sélection en mémoire reste la source de vérité de la session.
    }
  },

  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  reset: () => {
    set(initialState);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // La réinitialisation du store ne dépend pas du stockage du navigateur.
    }
  },
}));
